import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, Package, Users, Calendar, Building2,
  Camera, LogOut, WifiOff, BellRing, Clock, Mic,
} from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import Modal from "@/components/Modal";
import Logo from "@/components/Logo";
import PhotoCapture from "@/components/PhotoCapture";
import { api, API, getOfflineQueue, syncOfflineDevis, registerPush } from "@/lib/api";

const NAV = [
  { to: "/tableau-de-bord", key: "tableau", icon: LayoutDashboard, testid: "nav-tableau-de-bord", end: true },
  { to: "/devis", key: "devis", icon: FileText, testid: "nav-devis-factures" },
  { to: "/stocks", key: "stock", icon: Package, testid: "nav-stock-materiel" },
  { to: "/clients", key: "clients", icon: Users, testid: "nav-clients-chantiers" },
  { to: "/agenda", key: "agenda", icon: Calendar, testid: "nav-agenda-rdv" },
  { to: "/comptes-rendus", key: "comptes_rendus", icon: Mic, testid: "nav-comptes-rendus" },
  { to: "/entreprise", key: "entreprise", icon: Building2, testid: "nav-mon-entreprise" },
];

const MOBILE_NAV = [
  { to: "/tableau-de-bord", key: "accueil", icon: LayoutDashboard, testid: "mnav-accueil", end: true },
  { to: "/devis", key: "devis_court", icon: FileText, testid: "mnav-devis" },
  null,
  { to: "/stocks", key: "stock_court", icon: Package, testid: "mnav-stock" },
  { to: "/clients", key: "clients_court", icon: Users, testid: "mnav-clients" },
];

export default function Layout() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [photoOpen, setPhotoOpen] = useState(false);
  const [chantiers, setChantiers] = useState([]);
  const [chantierId, setChantierId] = useState("");
  const [offline, setOffline] = useState(!navigator.onLine);
  const [pending, setPending] = useState(getOfflineQueue().length);
  const location = useLocation();
  const [abo, setAbo] = useState(null);

  useEffect(() => {
    api.get("/abonnement")
      .then((r) => {
        setAbo(r.data);
        if (r.data.statut === "bloque" && location.pathname !== "/abonnement") navigate("/abonnement");
      })
      .catch(() => {});
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (photoOpen) api.get("/chantiers").then((r) => setChantiers(r.data)).catch(() => {});
  }, [photoOpen]);

  useEffect(() => {
    const sync = () => {
      syncOfflineDevis().then((n) => {
        const rest = getOfflineQueue().length;
        setPending(rest);
        if (n > 0) toast.success(t("nav.sync_ok", { count: n }));
        if (rest > 0 && navigator.onLine)
          toast.error(t("nav.sync_erreur", { count: rest }));
      });
    };
    const onOnline = () => { setOffline(false); sync(); };
    const onOffline = () => setOffline(true);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    if (navigator.onLine) sync();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [notifPrompt, setNotifPrompt] = useState(false);
  const [logoUrl, setLogoUrl] = useState("");

  useEffect(() => {
    api.get("/entreprise")
      .then((r) => setLogoUrl(r.data?.has_logo ? `${API}/entreprise/logo?t=${Date.now()}` : ""))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if ("Notification" in window && "PushManager" in window &&
        Notification.permission === "default" &&
        !localStorage.getItem("chantiersplus_notif_plus_tard")) {
      setNotifPrompt(true);
    }
  }, []);

  async function activerNotifs() {
    const ok = await registerPush();
    setNotifPrompt(false);
    if (ok) toast.success(t("nav.notif_ok"));
    else toast.error(t("nav.notif_ko"));
  }

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 h-12 px-4 rounded-lg text-sm font-medium transition-colors ${
      isActive ? "bg-[#221F1A] text-[#D9A461] border-l-2 border-[#D9A461]" : "text-stone-300 hover:text-white hover:bg-[#221F1A]"}`;

  return (
    <div className="min-h-screen bg-[#F7F4EE]">
      <aside className="hidden md:flex flex-col fixed inset-y-0 left-0 w-64 bg-[#12100D] z-40">
        <div className="flex items-center gap-3 px-5 h-20 border-b border-white/10">
          <Logo size={40} iconSize={22} logoUrl={logoUrl} />
          <div>
            <div className="font-serif font-bold text-lg text-white leading-tight">Mastoras</div>
            <div className="text-xs text-stone-400 truncate max-w-[150px]">{user?.nom}</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.end} className={linkCls} data-testid={item.testid}>
              <item.icon size={20} />
              {t(`nav.${item.key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button onClick={doLogout} data-testid="btn-deconnexion"
                  className="flex items-center gap-3 h-12 px-4 rounded-lg text-sm font-medium text-stone-400 hover:text-white hover:bg-[#221F1A] w-full">
            <LogOut size={20} /> {t("nav.deconnexion")}
          </button>
        </div>
      </aside>

      <header className="md:hidden sticky top-0 z-40 bg-[#12100D] h-16 flex items-center justify-between px-4">
        <div className="flex items-center gap-2">
          <Logo size={36} iconSize={19} logoUrl={logoUrl} />
          <span className="font-serif font-bold text-lg text-white">Mastoras</span>
        </div>
        <div className="flex items-center gap-1">
          <NavLink to="/agenda" data-testid="mnav-agenda" className="w-11 h-11 flex items-center justify-center text-stone-300">
            <Calendar size={22} />
          </NavLink>
          <NavLink to="/comptes-rendus" data-testid="mnav-comptes-rendus" className="w-11 h-11 flex items-center justify-center text-stone-300">
            <Mic size={22} />
          </NavLink>
          <NavLink to="/entreprise" data-testid="mnav-entreprise" className="w-11 h-11 flex items-center justify-center text-stone-300">
            <Building2 size={22} />
          </NavLink>
          <button onClick={doLogout} data-testid="mbtn-deconnexion" className="w-11 h-11 flex items-center justify-center text-stone-300">
            <LogOut size={22} />
          </button>
        </div>
      </header>

      {(offline || pending > 0) && (
        <div className="md:ml-64 bg-[#FCEFD9] text-[#8A5A0F] text-sm font-semibold px-4 py-2.5 flex items-center gap-2 border-b border-[#F6D9A9]"
             data-testid="bandeau-hors-ligne">
          <WifiOff size={16} className="shrink-0" />
          {offline ? t("nav.hors_ligne") : t("nav.sync_attente", { count: pending })}
        </div>
      )}

      {abo?.statut === "essai" && (
        <div className="md:ml-64 bg-[#221F1A] text-stone-200 text-sm px-4 py-2.5 flex items-center gap-3 border-b border-[#D9A461]/30"
             data-testid="bandeau-essai">
          <Clock size={16} className="text-[#D9A461] shrink-0" />
          <span className="flex-1">{t("nav.essai", { count: abo.jours_restants })}</span>
          <NavLink to="/abonnement" className="font-bold text-[#D9A461] h-9 px-3 flex items-center shrink-0"
                   data-testid="lien-abonnement-bandeau">{t("nav.sabonner")}</NavLink>
        </div>
      )}

      {notifPrompt && !offline && (
        <div className="md:ml-64 bg-[#12100D] text-stone-200 text-sm px-4 py-2.5 flex items-center gap-3 border-b border-[#D9A461]/30"
             data-testid="bandeau-notifications">
          <BellRing size={16} className="text-[#D9A461] shrink-0" />
          <span className="flex-1">{t("nav.notif_texte")}</span>
          <button onClick={activerNotifs} className="font-bold text-[#D9A461] h-9 px-3 shrink-0"
                  data-testid="btn-activer-notifications">{t("nav.notif_activer")}</button>
          <button onClick={() => { localStorage.setItem("chantiersplus_notif_plus_tard", "1"); setNotifPrompt(false); }}
                  className="text-stone-400 h-9 px-2 shrink-0" data-testid="btn-ignorer-notifications">{t("nav.notif_plus_tard")}</button>
        </div>
      )}

      <main className="md:ml-64 pb-24 md:pb-10 px-4 sm:px-6 lg:px-10 pt-6 max-w-6xl">
        <Outlet />
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[#12100D] h-16 grid grid-cols-5 border-t border-white/10">
        {MOBILE_NAV.map((item, i) =>
          item === null ? (
            <div key="fab" className="flex items-center justify-center">
              <button onClick={() => setPhotoOpen(true)} data-testid="btn-capture-photo"
                      className="w-14 h-14 -mt-6 rounded-full bg-[#D9A461] text-[#12100D] flex items-center justify-center border-4 border-[#F7F4EE] active:scale-95 transition-transform">
                <Camera size={24} />
              </button>
            </div>
          ) : (
            <NavLink key={item.to} to={item.to} end={item.end} data-testid={item.testid}
                     className={({ isActive }) =>
                       `flex flex-col items-center justify-center gap-0.5 text-[10px] font-medium ${isActive ? "text-[#D9A461]" : "text-stone-400"}`}>
              <item.icon size={22} />
              {t(`nav.${item.key}`)}
            </NavLink>
          )
        )}
      </nav>

      <Modal open={photoOpen} onClose={() => setPhotoOpen(false)} title={t("nav.photo_titre")} testid="modal-photo-rapide">
        <p className="text-sm text-stone-600 mb-4">{t("nav.photo_texte")}</p>
        <label className="block text-sm font-medium mb-1.5">{t("nav.photo_chantier")}</label>
        <select value={chantierId} onChange={(e) => setChantierId(e.target.value)}
                className="input-field mb-5" data-testid="select-chantier-photo">
          <option value="">{t("nav.aucun_chantier")}</option>
          {chantiers.map((c) => (
            <option key={c.id} value={c.id}>{c.titre}</option>
          ))}
        </select>
        <PhotoCapture testid="btn-prendre-photo" chantierId={chantierId} type="photo_chantier"
                      className="btn-accent w-full" onUploaded={() => setPhotoOpen(false)}>
          <Camera size={20} /> {t("nav.photo_ouvrir")}
        </PhotoCapture>
      </Modal>
    </div>
  );
}
